import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Filter, TextFilter, NumericFilter } from "@progress/kendo-react-data-tools";
import DashboardMultiSelect from "./DashboardMultiSelect";
import DashbaordDateFilter from "./DashbaordDateFilter";
import { Utils } from "../../helpers";

//!types of filters: text, select, date

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.log("Error caught by ErrorBoundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <h1>Something went wrong in the filter viewer.</h1>;
    }

    return this.props.children;
  }
}

const DashboardFilterViewer = ({
  restClient,
  setFilterCount,
  filterCount,
  filterConfiguration,
  applyFiltersFromViewer,
  allFilters,
  setShowFilter,
  loader,
  filterLogic,
  appNavigationDiv,
}) => {
  const [formattedFilter, setFormattedFilter] = useState({ logic: filterLogic, filters: [] }); //filters formatted for kendo filter component
  const multiSelectRefs = useRef({});
  const dateFilterRefs = useRef({});
  const filters = {
    date: useCallback(
      (props) => (
        <DashbaordDateFilter
          ref={(ref) => {
            if (ref) {
              dateFilterRefs.current[props.filter.uuid] = ref;
            } else if (dateFilterRefs.current[props.filter.uuid]) {
              delete dateFilterRefs.current[props.filter.field];
            }
          }}
          onFilterChange={onFilterChange}
          filterData={props.filter}
        />
      ),
      [DashbaordDateFilter],
    ),
    text: TextFilter,
    numeric: NumericFilter,
    select: useCallback(
      (props) => (
        <DashboardMultiSelect
          addLogicBetweenFilters={addLogicBetweenFilters}
          filterData={props.filter}
          loader={loader}
          onFilterChange={onFilterChange}
          allFilters={allFilters}
          ref={(ref) => {
            if (ref) {
              multiSelectRefs.current[props.filter.uuid] = ref;
            } else if (dateFilterRefs.current[props.filter.uuid]) {
              delete dateFilterRefs.current[props.filter.field];
            }
          }}
          restClient={restClient}
        />
      ),
      [DashboardMultiSelect],
    ),
  };
  const operators = {
    date: [
      { text: "Equals", operator: "==" },
      { text: "Less Than Equal To", operator: "<=" },
      { text: "Greater Than Equal To", operator: ">=" },
      { text: "Greater Than", operator: ">" },
      { text: "Less Than", operator: "<" },
      { text: "Year to Date", operator: "ytd" },
      { text: "Month to Date", operator: "mtd" },
      { text: "Between", operator: "gte&&lte" },
    ],
    numeric: [
      { text: "Equals", operator: "==" },
      { text: "Not Equals", operator: "!=" },
      { text: "Greater Than", operator: ">" },
      { text: "Lesser Than", operator: "<" },
    ],
    text: [
      { text: "Contains", operator: "LIKE" },
      { text: "Does Not Contain", operator: "NOT LIKE" },
    ],
    select: [
      { text: "Equals", operator: "==" },
      { text: "Not Equals", operator: "NOT LIKE" },
    ],
  };

  //setting values needed for the kendo filter componet and needed to apply the filters
  const mapFilterConfiguration = (filterConfiguration) => {
    filterConfiguration?.forEach((singleFilter) => {
      if (singleFilter?.dataType === "date" && !singleFilter?.value) {
        if (singleFilter?.startDate && singleFilter?.endDate)
          singleFilter.value = { startDate: new Date(singleFilter?.startDate), endDate: new Date(singleFilter?.endDate) };
        else if (singleFilter?.startDate) singleFilter.value = new Date(singleFilter?.startDate);
        else if (singleFilter?.endDate) singleFilter.value = new Date();
      }

      //setting the filters in a format required by kendo
      setFormattedFilter((prevFilter) => {
        const filterExists = prevFilter.filters.some((filter) => filter.uuid === singleFilter.uuid);
        if (!filterExists) {
          const newFilters = [
            ...prevFilter.filters,
            ...(singleFilter && singleFilter.field && singleFilter.operator && singleFilter.value
              ? [
                {
                  field: singleFilter.field,
                  operator: singleFilter.operator,
                  value: singleFilter.value,
                  fieldType: singleFilter.dataType,
                  uuid: prevFilter.filters.uuid || Utils.generateUUID(),
                },
              ]
              : []),
          ];
          return { ...prevFilter, filters: newFilters };
        }
        return prevFilter;
      });
    });
  };

  const onFilterChange = (e) => {
    //for kendo related changes
    if (e.filter) {
      e.filter.filters.forEach((filter, index) => {
        if (!filter.uuid) {
          e.filter.filters[index].uuid = Utils.generateUUID();
        }
        if (!filter.fieldType) {
          let tempFilter = allFilters.find((allFilterFilter) => allFilterFilter.field === e.filter.filters[e.filter.filters.length - 1].field);
          e.filter.filters[index].fieldType = tempFilter?.dataType;
        }
        // Handle date operator changes (when changing from ytd to equals, 2 values are sent)
        if (filter.fieldType === "date") {
          const prevFilter = formattedFilter.filters.find((f) => f.field === filter.field);
          //comparing prevFilter and currentFilter to see if the operator has changed
          const dateOperators = ["mtd", "ytd", "gte&&lte"];
          if (prevFilter && prevFilter.operator !== filter.operator && !dateOperators.includes(filter.operator)) {
            // If changing to equals, set single date value
            e.filter.filters[index].value = new Date();
          }
        }
        if (filter.fieldType === "select" && e.filter?.filters[index]?.value?.length > 0) {
          e.filter.filters[index].value.map((value) => {
            value.operator = filter.operator;
          });
        }
      });
      setFormattedFilter(e.filter);
    } else {
      //for custom changes to value from multiselect and date component
      setFormattedFilter((prev) => ({
        ...prev,
        filters: prev.filters.map((filter) => {
          if (filter.uuid === e.uuid) {
            return { ...filter, value: e.value };
          }
          return filter;
        }),
      }));
    }
  };

  // Function to clean up refs
  const cleanupRefs = (refs, filterUuids) => {
    if (refs && refs.current) {
      Object.keys(refs.current).forEach((key) => {
        if (refs.current[key]) {
          if (!filterUuids.has(key)) {
            delete refs.current[key];
          }
        }
      });
    }
  };

  //add logic between filter
  //divides the incoming array into two parts and adds the logic between all of them
  //needs to contains exactly 3 elements for elastic to be able to filter properly
  //[[fitlers], logic, [filters]]
  const addLogicBetweenFilters = (array, logic) => {
    const indexOfLastElementInFirstArr = Math.floor(array.length / 2);
    if (indexOfLastElementInFirstArr < 1 || typeof array[1] === "string") return array;
    else {
      const joinFiltersWithLogic = (startingIndex, endingIndex) => {
        return array.slice(startingIndex, endingIndex).reduce((acc, each, index, arr) => {
          arr.length === 1 ? acc.push(...each) : acc.push(each);
          index !== arr.length - 1 && acc.push(logic.toUpperCase());
          return acc;
        }, []);
      };
      return [
        joinFiltersWithLogic(0, indexOfLastElementInFirstArr),
        logic.toUpperCase(),
        joinFiltersWithLogic(indexOfLastElementInFirstArr, array.length),
      ];
    }
  };

  //formats the filters and then pushes it to dashboard manager
  //!need to clean up this function
  const prepareFilters = () => {
    let extractedFilters;

    //handling date filter
    let extractedDateFilters = [];

    //removing unecessary refs
    const filterUuids = new Set(formattedFilter.filters.map((filter) => filter.uuid));
    cleanupRefs(dateFilterRefs, filterUuids);
    cleanupRefs(multiSelectRefs, filterUuids);

    //ytd mtd gte&lte should be outside while every other date filter should be in one sub array
    if (dateFilterRefs.current) {
      Object.keys(dateFilterRefs.current).forEach((key) => {
        let currentValue = dateFilterRefs.current[key].getDateValues();
        extractedDateFilters = extractedDateFilters.concat(currentValue);
      });
      if (extractedDateFilters.length === 1) {
        extractedDateFilters = extractedDateFilters.flat(); //flattening the array only if it contains one ytd, mtd or gte&lte
      } else {
        extractedDateFilters = addLogicBetweenFilters(extractedDateFilters, formattedFilter.logic)
      }
    }

    //handling all fitlers other than multiselect and date
    let allExtractedFilter = formattedFilter.filters
      .filter((filter) => filter.value && filter.fieldType !== "date" && filter.fieldType !== "select") // Filter out entries with null values, date and select filters
      .map((filter, index, array) => {
        let a = [];
        Object.keys(filter).forEach((key) => {
          if (key === "field" || key === "operator" || key === "value") {
            if (key === "value" && typeof filter[key] !== "string") {
              a.push(JSON.stringify(filter[key])); //safer if value is always be a string
            } else {
              a.push(filter[key]);
            }
          }
        });
        return a;
      });

    allExtractedFilter = allExtractedFilter.length > 1 ? addLogicBetweenFilters(allExtractedFilter, formattedFilter.logic) : allExtractedFilter.flat();

    //handleMultiSelectFilter
    let extractedMultiSelectFilters = [];
    if (multiSelectRefs.current) {
      Object.keys(multiSelectRefs.current).forEach((key, index) => {
        extractedMultiSelectFilters = extractedMultiSelectFilters.concat(multiSelectRefs.current[key].getMultiSelectValues());
      });
      if (extractedMultiSelectFilters.length === 1) {
        extractedMultiSelectFilters = extractedMultiSelectFilters.flat();
      }
      extractedMultiSelectFilters = addLogicBetweenFilters(extractedMultiSelectFilters, formattedFilter.logic);
    }

    extractedFilters = [
      ...(extractedDateFilters.length > 0 ? [extractedDateFilters] : []),
      ...(allExtractedFilter.length > 0 ? [allExtractedFilter] : []),
      ...(extractedMultiSelectFilters.length > 0 ? [extractedMultiSelectFilters] : []),
    ];

    extractedFilters = addLogicBetweenFilters(extractedFilters, formattedFilter.logic);

    //change operator, fieldtype field
    filterConfiguration = formattedFilter.filters.reduce((acc, formattedFilter) => {
      const matchingFilter = allFilters
        .filter((filter) => filter && typeof filter === "object" && filter.filterName)
        .find((i) => i.field === formattedFilter.field);

      if (matchingFilter) {
        acc.push({
          ...matchingFilter,
          operator: formattedFilter.operator,
          field: formattedFilter.field,
          uuid: formattedFilter.uuid,
          value: formattedFilter.value,
        });
      }

      return acc;
    }, []);
    setFilterCount(formattedFilter?.filters?.length ?? 0);
    applyFiltersFromViewer(extractedFilters, filterConfiguration, allFilters, formattedFilter.logic);
  };

  useEffect(() => {
    const hasDefaultFilter = filterConfiguration.some((i) => i.isDefault);
    if (filterCount <= 0) {
      if (hasDefaultFilter) {
        mapFilterConfiguration?.(filterConfiguration.filter((i) => i.isDefault));
      } else {
        mapFilterConfiguration?.([filterConfiguration[0]]); //loading only the 1st filter to prevent all filters from loading
      }
    } else {
      mapFilterConfiguration?.(filterConfiguration);
    }

    // Modify the Filter component buttons
    const modifyFilterButtons = () => {
      //getting the current appp
      let currentAppOpen = document.getElementById(appNavigationDiv);
      // Find the button container
      const buttonContainer = currentAppOpen?.querySelector(".k-filter-toolbar") || document.querySelector(".k-filter-toolbar");
      if (buttonContainer) {
        // Find all buttons
        const buttons = buttonContainer.querySelectorAll("button");
        buttons.forEach((button) => {
          if (button.textContent.includes("Add Expression")) {
            button.textContent = "Add Filter";
          }
          if (button.textContent.includes("Add Group")) {
            button.style.display = "none";
          }
        });
      }
    };

    // Call the function to modify buttons
    modifyFilterButtons();
  }, []);

  return (
    <ErrorBoundary>
      <div className="filter-form-container">
        <div className="dashboard-filter-wrapper-viewer">
          <Button type="button" aria-label="Close" onClick={() => setShowFilter(false)}>
            <i className="fa fa-close" aria-hidden="true"></i>
          </Button>
          {/* pass classname to handle hiding better */}
          <Filter
            value={formattedFilter}
            onChange={onFilterChange}
            fields={(allFilters || [])
              .filter((filter) => filter && typeof filter === "object" && filter.filterName)
              .map((filter) => {
                if (typeof filter === "object") {
                  return {
                    name: filter.field,
                    label: filter.filterName,
                    filter: filters[filter.dataType],
                    operators: operators[filter.dataType],
                  };
                }
              })}
          />
          <Button style={{ width: "100px" }} onClick={prepareFilters} themeColor={"primary"}>
            Apply
          </Button>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default DashboardFilterViewer;
